import React from "react";
import '../App.css';

const PrivacyPolicy = () => {
  return (
    <div id="privacyPolicy" className="container py-5">
      <div className="row">
        <div id="enlargedText" className="col-lg-15 col-xm-12">
          <h1 className="about-heading">Privacy Policy</h1>

          <p><strong>Effective Date:</strong> November 3, 2026</p>

          <h4>Introduction</h4>
          <p>
            MYAPPS LABS LTD ("myapps.", "we," "our," or "us") develops and provides mobile applications,
            including remote control apps, casting and mirroring apps, and games.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, and protect your information when you use our applications.
            By using our applications, you agree to this policy.
          </p>
          <p>
            Our apps are distributed through the Apple App Store, Google Play, and Huawei AppGallery.
            Depending on the store from which you downloaded the app, different third-party services may apply,
            as described below.
          </p>

          <h4>Information We Collect</h4>

          <h5>Device & Network Information</h5>
          <ul>
            <li>IP address</li>
            <li>Wi-Fi status and network type</li>
            <li>Device model, operating system, and language</li>
          </ul>
          <p>
            This information is used strictly for app functionality, including connecting to devices such as TVs and streamers.
          </p>

          <h5>Advertising Data</h5>
          <p>
            Some of our apps display ads using third-party ad networks.
            The ad networks depend on the app store from which you downloaded the app:
          </p>
          <ul>
            <li>
              <strong>Google Play and Apple App Store versions</strong> use Google AdMob.
              Through AdMob mediation, ads may also be served by partner networks: Meta Audience Network,
              Pangle, and the Yandex Advertising Network (the Yandex SDK includes the AppMetrica analytics component).
              These networks may collect advertising identifiers (GAID on Android, IDFA on iOS, where permitted),
              device information, IP address, approximate location derived from the IP address (country or city level),
              and ad interaction data (impressions, clicks), in order to deliver, measure, and, where permitted, personalize ads.
            </li>
            <li>
              <strong>Huawei AppGallery versions</strong> use HUAWEI Ads Kit (Petal Ads) and, in some regions,
              the Yandex Advertising Network (including its AppMetrica component).
              These services may collect the Open Advertising Identifier (OAID),
              device information (model, OS version), IP address, approximate location derived from the IP address,
              basic app information (package name, version), and ad interaction data (impressions, clicks).
              This data is used for ad delivery, measurement, attribution, and fraud prevention.
              For more information, see the
              {" "}<a href="https://consumer.huawei.com/minisite/cloudservice/aaid/en/privacy-statement.htm" target="_blank" rel="noopener noreferrer">HUAWEI Ads Kit Privacy Statement</a>
              {" "}and the{" "}
              <a href="https://yandex.com/legal/confidential/" target="_blank" rel="noopener noreferrer">Yandex Privacy Policy</a>.
            </li>
          </ul>
          <p>
            On iOS, we request your permission through Apple's App Tracking Transparency framework before any tracking that requires it.
            Users in certain regions may be asked for consent before personalized ads are shown.
            You may reset or opt out of advertising identifiers through your device settings.
          </p>

          <h5>Analytics & Crash Data</h5>
          <p>
            We use Firebase services such as Analytics and Crashlytics on our Google Play and Apple App Store apps
            to understand app usage and improve performance.
          </p>
          <ul>
            <li>App usage events</li>
            <li>Crash logs and diagnostics</li>
          </ul>
          <p>
            Our Huawei AppGallery apps do not use Firebase. Where the Yandex Advertising Network is active,
            its AppMetrica component may collect anonymous device, diagnostic, and ad-performance data
            as described in the Yandex Privacy Policy. The same AppMetrica component is included in our
            Google Play and Apple App Store apps as part of the Yandex SDK.
          </p>

          <h5>Remote Configuration</h5>
          <p>
            We use remote configuration services to adjust app settings without releasing new versions.
            Google Play and Apple App Store apps use Firebase Remote Config.
            Huawei AppGallery apps fetch a JSON configuration file hosted on Microsoft Azure Blob Storage.
            No personal data is collected through remote configuration.
          </p>

          <h5>In-App Purchases & Subscriptions</h5>
          <p>
            Some apps offer subscriptions or one-time purchases.
            Payments are processed by the app store from which you downloaded the app
            (Apple App Store, Google Play, or Huawei AppGallery).
            We do not collect or store payment information.
            Subscription management on Google Play and Apple App Store apps is handled through RevenueCat,
            which processes anonymous transaction identifiers to manage your subscription status.
          </p>

          <h5>Permissions</h5>
          <p>
            Depending on the app, we may request permissions such as:
          </p>
          <ul>
            <li>Local network / Wi-Fi access – to connect to devices</li>
            <li>Storage or photos – to save user-generated content</li>
          </ul>
          <p>Permissions are only used for core app functionality.</p>

          <h4>Legal Basis (GDPR)</h4>
          <ul>
            <li>Consent – for ads and tracking (where required)</li>
            <li>Legitimate interest – for app functionality and analytics</li>
            <li>Contract – for subscriptions and purchases</li>
          </ul>

          <h4>Data Retention</h4>
          <p>
            We retain data only as long as necessary to provide our services and comply with legal obligations.
            Third-party services we integrate with (AdMob, Meta Audience Network, Pangle, Yandex, Firebase,
            HUAWEI Ads Kit, RevenueCat) retain data according to their own policies, linked below.
          </p>

          <h4>Data Sharing with Third Parties</h4>
          <p>
            We share limited data with the following third parties strictly for the purposes described above:
          </p>
          <ul>
            <li>Google (AdMob, Firebase, Google Play Services) – Google Play and Apple App Store apps</li>
            <li>Meta Platforms (Meta Audience Network) – advertising, Google Play and Apple App Store apps</li>
            <li>ByteDance (Pangle) – advertising, Google Play and Apple App Store apps</li>
            <li>Yandex (Yandex Advertising Network, AppMetrica) – advertising and ad analytics, all stores</li>
            <li>Apple (App Store Services) – Apple App Store apps</li>
            <li>Huawei (HUAWEI Ads Kit / Petal Ads) – Huawei AppGallery apps</li>
            <li>Microsoft Azure – remote configuration hosting for Huawei AppGallery apps</li>
            <li>RevenueCat – subscription management for Google Play and Apple App Store apps</li>
          </ul>
          <p>
            We do not sell your personal information to any third party.
          </p>

          <h4>Third-Party Services</h4>
          <ul>
            <li><a href="https://www.google.com/policies/privacy/" target="_blank" rel="noopener noreferrer">Google Play Services</a></li>
            <li><a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noopener noreferrer">AdMob</a></li>
            <li><a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">Meta Audience Network (Meta Privacy Policy)</a></li>
            <li><a href="https://www.pangleglobal.com/privacy/enduser-en" target="_blank" rel="noopener noreferrer">Pangle</a></li>
            <li><a href="https://yandex.com/legal/confidential/" target="_blank" rel="noopener noreferrer">Yandex Advertising Network / AppMetrica</a></li>
            <li><a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase</a></li>
            <li><a href="https://consumer.huawei.com/minisite/cloudservice/aaid/en/privacy-statement.htm" target="_blank" rel="noopener noreferrer">HUAWEI Ads Kit</a></li>
            <li><a href="https://consumer.huawei.com/en/privacy/privacy-policy/" target="_blank" rel="noopener noreferrer">Huawei Consumer Privacy</a></li>
            <li><a href="https://www.revenuecat.com/privacy/" target="_blank" rel="noopener noreferrer">RevenueCat</a></li>
            <li><a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">Microsoft Azure</a></li>
            <li>Apple App Store Services</li>
          </ul>

          <h4>International Data Transfers</h4>
          <p>
            Your information may be processed outside your country of residence depending on the services used.
            Google, Meta, Pangle, and Firebase data may be processed in the United States and other regions.
            Yandex and AppMetrica data may be processed in the Russian Federation and other regions in which Yandex operates.
            HUAWEI Ads Kit data is processed in the region selected during app configuration (such as Germany for European users).
            Microsoft Azure data is processed in the Azure region selected for our configuration service.
            All such transfers are governed by the privacy policies of the respective service providers
            and applicable data protection laws.
          </p>

          <h4>Your Rights</h4>
          <p>Depending on your region, you may have rights to:</p>
          <ul>
            <li>Access your data</li>
            <li>Request deletion</li>
            <li>Opt out of personalized ads</li>
            <li>Reset your advertising identifier through device settings</li>
          </ul>
          <p>Contact us to exercise these rights.</p>

          <h4>Children's Privacy</h4>
          <p>
            Our apps are not intended for children under 13.
            We do not knowingly collect personal data from children.
          </p>

          <h4>Security</h4>
          <p>
            We use reasonable measures to protect your data but cannot guarantee absolute security.
          </p>

          <h4>Changes</h4>
          <p>
            We may update this policy. Continued use of our apps means you accept the updated version.
            The "Effective Date" at the top of this policy reflects the most recent update.
          </p>

          <h4>Contact Us</h4>
          <p>
            Email: <a href="mailto:matanyamindevelop@gmail.com">matanyamindevelop@gmail.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;